import { useContext, useEffect, useRef } from 'react';
import Adjust from '@adjustcom/adjust-web-sdk';
import environment from '../utils/environment';
import { AppConfigContext } from '../contexts/appConfigContext';
import { logInfo } from '../utils/remoteLogger';

export interface IAdjustEvent {
  eventToken: string;
}

const appToken = {
  KRY: '32eju87tz85c',
  LIVI: 'z8btc2oioohs',
};

const TRACKING_KEYS = [
  'gclid',
  'gbraid',
  'wbraid',
  'fbclid',
  'ScCid',
  'ttclid',
  'msclkid',
];

const localEvents = {
  kry: {
    REGISTRATION: { eventToken: 'h09w47' },
  },

  livi: {
    REGISTRATION: { eventToken: 'ca7r7f' },
  },
};

// Keep track of Adjust internal states as the SDK doesn't seem to do that,
// and throws errors if the same state is set twice.
let isEnabled = false;
let isOffline = false;

const useAdjustTracker = () => {
  const { appConfig, userId } = useContext(AppConfigContext);

  const appConfigRef = useRef(appConfig);
  const userIdRef = useRef(userId);

  const event = environment.IS_KRY ? localEvents.kry : localEvents.livi;

  const getTrackingIds = () => {
    if (typeof window === 'undefined') return {};
    const trackingParams: Record<string, string> = {};
    const params = new URLSearchParams(window.location.search);
    TRACKING_KEYS.forEach((key) => {
      const value = params?.get(key);
      if (value) {
        trackingParams[key] = value;
      }
    });

    return trackingParams;
  };

  const registerAdTrackingIds = () => {
    const trackingIds = getTrackingIds();
    if (Object.keys(trackingIds).length > 0) {
      const callbackParams = Object.entries(trackingIds).map(
        ([key, value]) => ({
          key,
          value,
        })
      );
      // Given that adjust does not support sharing data with ad networks for web,
      // we need to manually pass the gclid so the analytics team can include it to the report they generate manually for google.
      // https://help.adjust.com/en/article/data-sharing-ad-network#networks-integrated-with-adjust
      Adjust.addGlobalCallbackParameters(callbackParams);
    }
  };

  const initialize = () => {
    Adjust.initSdk({
      appToken: environment.IS_KRY ? appToken.KRY : appToken.LIVI,
      environment: environment.IS_PROD ? 'production' : 'sandbox',
      logLevel: environment.IS_PROD ? 'none' : 'verbose',
    });

    // Extract and set the adjust_referrer if present (separate from tracking IDs)
    const params = new URLSearchParams(window.location.search);
    const adjustReferrer = params.get('adjust_referrer');

    if (adjustReferrer) {
      // URLSearchParams.get() automatically decodes the value
      // e.g., from "adjust_reftag%3DckqoUi50fVdII" to "adjust_reftag=ckqoUi50fVdII"
      // setReferrer expects a URL-encoded string, so we need to re-encode it
      logInfo('useAdjustTracker', 'Setting referrer', {
        decoded: adjustReferrer,
        encoded: encodeURIComponent(adjustReferrer),
      });
      Adjust.setReferrer(encodeURIComponent(adjustReferrer));
    }
    registerAdTrackingIds();
    isEnabled = true;
  };

  const enableTracking = () => {
    if (!isEnabled) {
      Adjust.restart();
      isEnabled = true;
    }
  };

  const disableTracking = () => {
    if (isEnabled) {
      Adjust.stop();
      isEnabled = false;
    }
  };

  const enableOfflineMode = () => {
    if (!isOffline) {
      Adjust.switchToOfflineMode();
      isOffline = true;
    }
  };

  const disableOfflineMode = () => {
    if (isOffline) {
      Adjust.switchBackToOnlineMode();
      isOffline = false;
    }
  };

  const trackEvent = (adjustEvent: IAdjustEvent, explicitUserId?: string) => {
    Adjust.trackEvent({
      eventToken: adjustEvent.eventToken,
      callbackParams: [
        { key: 'app_country', value: environment.COUNTRY },

        // Conditionally set the "userId" value if a userId is available.
        ...(explicitUserId || userIdRef.current
          ? [{ key: 'userId', value: explicitUserId || userIdRef.current }]
          : []),
      ],
    });
  };

  // Update mutable refs.
  useEffect(() => {
    appConfigRef.current = appConfig;
    userIdRef.current = userId;
  }, [appConfig, userId]);

  return useRef({
    event,

    initialize,
    enableTracking,
    disableTracking,
    enableOfflineMode,
    disableOfflineMode,
    trackEvent,
  }).current;
};

export default useAdjustTracker;
